import React from 'react';
import { Box, Avatar, Typography, Container, Stack, IconButton, Chip, CircularProgress, Alert } from '@mui/material';
import { CustomButton } from '../../common-components';
import senaface from '../../assets/paige.jpeg';
import { ArrowBackIos, CallEnd, Mic, MicOff } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import DotLoader from '../../common-components/DotLoader';
import useCallHandler from './hooks/useCallHandler';
import { CallStatus } from './slice/constants';
import CircularAudioSpectrum from './components/CircularAudioSpectrum';
import CallIcon from '@mui/icons-material/Call';
import { ROUTE_PATHS } from '../../routes/constants';

export default function CallPage() {
  const navigate = useNavigate();
  const { callStatus, handleStartCall, handleEndCall, handleToggleMute, isMuted, remoteAudioRef, backendStatus, spectrumColors, spectrumStream, shouldShowSpectrum, minutes, seconds, isRunning, error } = useCallHandler();

  const maximumCallDurationMinutes = parseInt(process.env.REACT_APP_PUBLIC_MAXIMUM_CALL_DURATION_MINUTES || '0', 10);
  const remainingSeconds = maximumCallDurationMinutes * 60 - (minutes * 60 + seconds);

  return (
    <Box
      sx={{
        height: '100vh',
        backgroundColor: 'background.default',
        display: 'flex',
        width: '100%',
      }}
    >
      {/* Main Content */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
        <Container maxWidth="lg" sx={{ flex: 1, display: 'flex', flexDirection: 'column', pt: 2 }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <IconButton onClick={() => navigate(ROUTE_PATHS.PORTAL)}>
                <ArrowBackIos />
              </IconButton>
              <Avatar sx={{ width: 24, height: 24 }} src={senaface} />
              <Typography variant="body2">Sena</Typography>
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  bgcolor: 'secondary.main',
                }}
              />
            </Stack>
            <Stack direction="row" spacing={1} alignItems="center">
              {isRunning && (
                <Chip
                  size="small"
                  sx={{ p: 2, gap: 0.5 }}
                  variant="outlined"
                  icon={<CallIcon fontSize="small" />}
                  label={
                    <Typography variant="caption" sx={{ lineHeight: 1, margin: 0 }}>
                      {minutes.toString().padStart(2, '0')}:{seconds.toString().padStart(2, '0')}
                    </Typography>
                  }
                />
              )}

              {/* <Select size="small" value="English">
                <MenuItem value="English">English</MenuItem>
              </Select> */}
              {/* <IconButton size="small">T</IconButton> */}
            </Stack>
          </Box>

          <Box
            sx={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {/* callStatus === CallStatus.CONNECTED &&  */}
            <Stack spacing={2} alignItems="center">
              <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {backendStatus === CallStatus.THINKING && <CircularProgress color="warning" thickness={2} size={200} sx={{ position: 'absolute', top: -10, left: -10, right: 0, bottom: 0, zIndex: 1, animationDuration: '10s' }} />}
                {shouldShowSpectrum && <CircularAudioSpectrum stream={spectrumStream} size={340} innerRadius={180} barCount={180} lineWidth={0} gradientColors={spectrumColors} glow smoothing={0.75} fftSize={1024} intensity={3.0} waveSpeed={30} style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', zIndex: 1, pointerEvents: 'none' }} />}

                <Avatar
                  sx={{
                    width: 180,
                    height: 180,
                    border: '8px solid white',
                    boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                  }}
                  src={senaface}
                />
              </Box>
              <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                Sena
              </Typography>

              {callStatus === CallStatus.IDLE && (
                <CustomButton sx={{ borderRadius: '24px', px: 3, py: 1.25 }} onClick={handleStartCall}>
                  Start a call
                </CustomButton>
              )}
              {callStatus === CallStatus.CONNECTING && (
                <Chip
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Typography variant="caption">Connecting</Typography>
                      <DotLoader />
                    </Box>
                  }
                />
              )}

              {callStatus === CallStatus.CONNECTED && (
                <Chip
                  size="medium"
                  sx={{ p: 2 }}
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      {isMuted ? (
                        <Typography variant="body2">Muted</Typography>
                      ) : (
                        <>
                          <Typography variant="body2">{backendStatus}</Typography>
                          <DotLoader />
                        </>
                      )}
                    </Box>
                  }
                />
              )}

              {callStatus === CallStatus.CONNECTED && (
                <Box sx={{ display: 'flex', gap: 3, pt: 2 }}>
                  <IconButton onClick={handleToggleMute} size="large" color="primary" sx={{ width: 72, height: 72, bgcolor: 'primary.contrastText', '&:hover': { bgcolor: 'primary.light' } }}>
                    {isMuted ? <MicOff fontSize="large" /> : <Mic fontSize="large" />}
                  </IconButton>
                  <IconButton onClick={() => handleEndCall({ silent: false })} size="large" color="error" sx={{ width: 72, height: 72, bgcolor: 'error.contrastText', '&:hover': { bgcolor: 'error.light' } }}>
                    <CallEnd fontSize="large" />
                  </IconButton>
                </Box>
              )}
              {callStatus === CallStatus.ERROR && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 2, alignItems: 'center', justifyContent: 'center' }}>
                  <Chip label={error || 'An error occurred! Please try again.'} color="error" />
                  <CustomButton sx={{ borderRadius: '24px', px: 3, py: 1.25 }} onClick={handleStartCall}>
                    Start a call
                  </CustomButton>
                </Box>
              )}
              {callStatus === CallStatus.DISCONNECTED && <Chip label="Disconnected" color="error" />}
              {backendStatus !== CallStatus.TIMEOUT && callStatus !== CallStatus.DISCONNECTED && remainingSeconds > 0 && remainingSeconds <= 60 && (
                <Alert severity="warning">
                  <Typography variant="body2">Your call wii be ended in {60 - seconds} seconds automatically.</Typography>
                </Alert>
              )}
            </Stack>
          </Box>

          <Box sx={{ pb: 2 }}>
            <Typography variant="caption" sx={{ textAlign: 'center', display: 'block' }}>
              AITiger can be inaccurate. Please verify all information before making any decisions.
            </Typography>
          </Box>
          <audio ref={remoteAudioRef} autoPlay playsInline></audio>
        </Container>
      </Box>
    </Box>
  );
}
